import { Grid } from '@mui/material';
import { Checkbox, HSpacer, RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import { useState } from 'react';

import { RoutalInfoIcon } from '@nx-smartmonkey/ui';
import SimpleWarningIcon from '../../../assets/svg/SimpleWarningIcon';
import { HeaderContent } from '../../../components/HeaderContent';
import { UnderLicenseClaim } from '../../../components/new-layout/UnderLicenseClaim';
import { Project } from '../../../domain/project/Project';
import { ProjectDriversConfiguration } from '../../../domain/project/projectDrivers/ProjectDriversConfiguration';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import { ProjectDriversMapper } from '../../../mappers/project/ProjectDriversMapper';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';

const ConfigurationPill = ({
  field,
  checked,
  onToggle,
  subOptions,
  isSubOption = false,
  disabled = false,
}: {
  field: ProjectDriversConfiguration;
  checked?: boolean;
  onToggle?: () => void;
  subOptions?: React.ReactNode[];
  isSubOption?: boolean;
  disabled?: boolean;
}) => {
  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        maxWidth: `760px`,
        padding: isSubOption ? undefined : `16px`,
        borderRadius: `10px`,
        border: isSubOption ? undefined : `1px solid ${RoutalPalette.neutral10}`,
        marginTop: isSubOption ? `12px` : `24px`,
      }}
    >
      <Grid style={{ opacity: disabled ? `60%` : undefined }}>
        <Grid container direction="row" wrap="nowrap" alignItems="center">
          {onToggle && (
            <>
              <Checkbox checked={checked} onChange={onToggle} disabled={disabled} />
              <HSpacer small />
            </>
          )}
          <Text variant="body-large" intlId={`drivers.app.configuration.${field}`} />
        </Grid>
        <VSpacer small />
        <Text variant="label" intlId={`drivers.app.configuration.${field}.description`} style={{ marginLeft: `3px` }} />
      </Grid>
      {subOptions && <Grid style={{ marginLeft: `20px` }}>{subOptions}</Grid>}
    </Grid>
  );
};

interface DriversConfigurationProps {
  project: Project;
}

export const DriversConfiguration = ({ project }: DriversConfigurationProps) => {
  const { data: user } = useRetrieveUser();
  const { run: editProject } = useUpdateProject({ project });
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const [isModalConfirmPin, setIsModalConfirmPin] = useState(false);

  if (!userCredentials?.subscription.test(`drivers.advanced_pod`)) {
    return (
      <UnderLicenseClaim headerText={intl.formatMessage({ id: `drivers.settings.under_gorilla_claim.title` })}>
        <Grid container direction="column" alignItems="center">
          <VSpacer large />
          <VSpacer large />
          <VSpacer large />
          <VSpacer large />
          <VSpacer medium />

          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ maxWidth: `700px` }}
          >
            <VSpacer large />
            <Text
              style={{ textAlign: `center`, color: `#5073B5` }}
              variant="h6"
              intlId="drivers.settings.under_gorilla_claim.header"
            />
          </Grid>
        </Grid>
      </UnderLicenseClaim>
    );
  }

  const toggleConfigurationField = (fields: ProjectDriversConfiguration[]) => {
    const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

    fields.forEach((field) => {
      projectDrivers.configuration = projectDrivers.configuration.includes(field)
        ? projectDrivers.configuration.filter((c) => c !== field)
        : [...projectDrivers.configuration, field];
    });

    editProject({
      drivers: ProjectDriversMapper.toDomain({
        ...projectDrivers,
        stop_fields: projectDrivers.stop_fields,
      }),
    });
  };

  return (
    <>
      {/* Pin confirmation modal */}
      <RoutalDialog
        open={isModalConfirmPin}
        onCloseDialog={() => setIsModalConfirmPin(false)}
        titleContent={
          <Grid>
            <Text variant="h6" intlId="drivers.app.configuration.pin.modal.title" />

            <VSpacer small />

            <Text
              variant="button"
              onClick={() => {
                window.open(
                  retrieveDoc({ supportPage: `communicationCustomers`, language: user?.language.value }),
                  `_blank`
                );
              }}
            >
              <Grid container alignItems="center">
                <RoutalInfoIcon style={{ width: `18px` }} color={RoutalPalette.primary40} />

                <HSpacer small />

                <Text
                  variant="button"
                  intlId="drivers.app.configuration.pin.modal.more_info"
                  style={{ fontSize: `14px` }}
                />
              </Grid>
            </Text>
          </Grid>
        }
        children={
          <Grid
            container
            flexDirection="row"
            flexWrap="nowrap"
            alignItems="center"
            padding="16px"
            style={{ backgroundColor: RoutalPalette.warning.light, borderRadius: `10px` }}
          >
            <Grid
              style={{
                display: `flex`,
                alignItems: `center`,
                justifyContent: `center`,
                borderRadius: `50px`,
                padding: `5px`,
                minHeight: `25px`,
                minWidth: `25px`,
                backgroundColor: RoutalPalette.primary40,
              }}
            >
              <SimpleWarningIcon style={{ color: RoutalPalette.neutral00 }} />
            </Grid>
            <HSpacer large />
            <Text variant="body-medium" intlId="drivers.app.configuration.pin.modal.description" />
          </Grid>
        }
        footerContent={
          <Grid container flexDirection="row" justifyContent="flex-end" width="100%">
            <Button
              variant="flat"
              style={{ padding: `14px 28px` }}
              intlId="cancel"
              onClick={() => setIsModalConfirmPin(false)}
            />
            <HSpacer medium />
            <Button
              variant="default"
              style={{ padding: `14px 28px` }}
              intlId="confirm"
              onClick={() => {
                toggleConfigurationField([`pin`]);
                setIsModalConfirmPin(false);
              }}
            />
          </Grid>
        }
      />

      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ height: `100%`, backgroundColor: RoutalPalette.neutral00 }}
      >
        <HeaderContent
          titleIntlId="drivers.app.configuration.title"
          styles={{
            container: {
              padding: `24px`,
              backgroundColor: RoutalPalette.white,
              borderBottom: `1px solid ${RoutalPalette.neutral10}`,
              maxHeight: `120px`,
            },
          }}
          // subtitle="drivers.app.configuration.subtitle"
          // article={{
          //   intlId: `drivers.app.configuration.article`,
          //   link: retrieveDoc({ supportPage: `communicationCustomers`, language: user?.language.value }),
          // }}
          // video={{
          //   intlId: `watch_video`,
          //   link: `https://www.youtube.com/watch?v=ZdZVx0ahpEI&t=137s`,
          // }}
        />

        <Grid
          style={{
            overflowY: `auto`,
            padding: `24px`,
            maxHeight: `100%`,
          }}
        >
          {/* Permissions section */}
          <Grid style={{ marginBottom: `50px` }}>
            <Text variant="h6" intlId={`drivers.app.configuration.permissions`} />
            {([`stop_duration`, `pin`, `driver_position`] as ProjectDriversConfiguration[]).map((field) => (
              <ConfigurationPill
                field={field}
                checked={project.drivers.configuration.includes(field)}
                onToggle={() => {
                  const isEnabled = !!project.drivers.configuration.find((value) => value === field);

                  if (field === `pin` && !isEnabled) {
                    setIsModalConfirmPin(true);
                  } else {
                    toggleConfigurationField([field]);
                  }
                }}
              />
            ))}

            {([`reorder_route`] as ProjectDriversConfiguration[]).map((field) => (
              <ConfigurationPill
                field={field}
                checked={project.drivers.configuration.includes(field)}
                onToggle={() => {
                  const fields = ([`manage_stops`] as ProjectDriversConfiguration[]).filter(
                    (subField) => !!project.drivers.configuration.find((value) => value === subField)
                  );

                  fields.push(field);
                  toggleConfigurationField(fields);
                }}
                subOptions={([`manage_stops`] as ProjectDriversConfiguration[]).map((subField) => (
                  <ConfigurationPill
                    field={subField}
                    checked={project.drivers.configuration.includes(subField)}
                    onToggle={() => {
                      toggleConfigurationField([subField]);
                    }}
                    isSubOption
                    disabled={!project.drivers.configuration.includes(field)}
                  />
                ))}
              />
            ))}

            {project.tasks_enabled &&
              ([`manage_tasks`] as ProjectDriversConfiguration[]).map((field) => (
                <ConfigurationPill
                  field={field}
                  checked={project.drivers.configuration.includes(field)}
                  onToggle={() => {
                    toggleConfigurationField([field]);
                  }}
                />
              ))}

            <ConfigurationPill
              field={`visualize_plans`}
              subOptions={[
                ([`visualize_planning_plans`, `visualize_finished_plans`] as ProjectDriversConfiguration[]).map(
                  (field) => (
                    <ConfigurationPill
                      field={field}
                      checked={project.drivers.configuration.includes(field)}
                      onToggle={() => {
                        toggleConfigurationField([field]);
                      }}
                      isSubOption
                    />
                  )
                ),
              ]}
            />
          </Grid>

          {/* POD section */}
          <Grid style={{ marginBottom: `50px` }}>
            <Text variant="h6" intlId={`drivers.app.configuration.pod`} />

            {([`pictures_gallery`] as ProjectDriversConfiguration[]).map((field) => (
              <ConfigurationPill
                field={field}
                checked={project.drivers.configuration.includes(field)}
                onToggle={() => {
                  toggleConfigurationField([field]);
                }}
              />
            ))}
          </Grid>

          {/* Survey section */}
          <Grid style={{ marginBottom: `50px` }}>
            <Text variant="h6" intlId={`drivers.app.configuration.survey`} />

            {([`survey`] as ProjectDriversConfiguration[]).map((field) => (
              <ConfigurationPill
                field={field}
                checked={project.drivers.configuration.includes(field)}
                onToggle={() => {
                  toggleConfigurationField([field]);
                }}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
